import { z } from 'zod';
import { TypedApiClient } from '../index';
import { schemas } from '../../generated/api-client';
import { apiPath } from '../path';
import { idWindowPageSchema, type IdWindowPage, type IdWindowParams } from '../id-window';

// Import schemas from generated
const {
  CreateSaleDto,
  UpdateSaleDto,
  MarkSalePaidDto,
  MarkSalePaidResponseDto,
  DeleteResponseDto,
  CreateSaleReturnDto,
  ReturnFirestoreResponse,
  SaleFirestoreResponse,
  CartItemFirestoreResponse,
  VariantDto,
  FiscalReceipt,
  ReturnInfoDto,
  ModifierDto,
} = schemas;

// ====== Generated Schemas ======
export const CartItemSchema = CartItemFirestoreResponse;
export const SaleResponseSchema = SaleFirestoreResponse;
export const VariantSchema = VariantDto;
export const FiscalInfoSchema = FiscalReceipt;
export const ReturnInfoSchema = ReturnInfoDto;
export const ModifierSchema = ModifierDto;
export const CreateSaleSchema = CreateSaleDto;
export const UpdateSaleSchema = UpdateSaleDto;
export const MarkSalePaidSchema = MarkSalePaidDto;
export const MarkSalePaidResponseSchema = MarkSalePaidResponseDto;
export const DeleteResponseSchema = DeleteResponseDto;
export const CreateSaleReturnSchema = CreateSaleReturnDto;
export const SaleReturnResponseSchema = ReturnFirestoreResponse;

// Type exports (derived from generated schemas)
export type SaleResponse = z.infer<typeof SaleResponseSchema>;
export type CreateSale = z.infer<typeof CreateSaleSchema>;
export type DeleteResponse = z.infer<typeof DeleteResponseSchema>;
export type UpdateSale = z.infer<typeof UpdateSaleSchema>;
export type MarkSalePaid = z.infer<typeof MarkSalePaidSchema>;
export type MarkSalePaidResponse = z.infer<typeof MarkSalePaidResponseSchema>;
export type CreateSaleReturn = z.infer<typeof CreateSaleReturnSchema>;
export type SaleReturnResponse = z.infer<typeof SaleReturnResponseSchema>;

// Legacy type exports for backwards compatibility
export type CartItem = z.infer<typeof CartItemSchema>;
export type Variant = z.infer<typeof VariantSchema>;
export type FiscalInfo = z.infer<typeof FiscalInfoSchema>;
export type ReturnInfo = z.infer<typeof ReturnInfoSchema>;
export type Modifier = z.infer<typeof ModifierSchema>;

// ====== Return Comparison Schema ======
// Note: This is a custom schema not in generated (comparison endpoints are utility)
export const ReturnComparisonResultSchema = z.object({
  exists: z.boolean(),
  supabaseData: SaleReturnResponseSchema.nullable(),
}).passthrough();

export type ReturnComparisonResult = z.infer<typeof ReturnComparisonResultSchema>;

// API factory function
export function createSalesApi(client: TypedApiClient) {
  return {
    /**
     * Create a new sale
     * @throws ApiValidationError if request data is invalid
     * @throws ApiRequestError if server returns an error
     */
    createSale: (data: CreateSale): Promise<SaleResponse> => {
      return client.post('/sales', {
        data,
        requestSchema: CreateSaleSchema, // Pre-flight validation
        responseSchema: SaleResponseSchema,
      });
    },

    /**
     * Get sales list with optional filters
     */
    getSales: (params?: {
      storeId?: string;
      saleType?: 'product' | 'service' | 'mixed';
      startDate?: string;
      endDate?: string;
      /** Firestore ID or Supabase UUID; a client the organization does not have matches no sales. */
      clientId?: string;
      limit?: number;
      offset?: number;
    }): Promise<SaleResponse[]> => {
      return client.get('/sales', {
        params: params as Record<string, unknown>,
        responseSchema: z.array(SaleResponseSchema),
      });
    },

    /**
     * ATL-1477: one snapshot window of `/sales` — see `../id-window.ts`. Use it through
     * `readWholeList`, never directly: a single window is not the list.
     */
    getSalesWindow: (
      params: {
      storeId?: string;
      saleType?: 'product' | 'service' | 'mixed';
      startDate?: string;
      endDate?: string;
      includeDeleted?: 'true';
      clientId?: string;
      } & IdWindowParams,
    ): Promise<IdWindowPage<z.infer<typeof SaleResponseSchema>>> => {
      return client.get('/sales/window', {
        params: params as Record<string, unknown>,
        responseSchema: idWindowPageSchema(SaleResponseSchema),
      });
    },

    /**
     * Get a single sale by ID
     */
    getSaleById: (id: string): Promise<SaleResponse> => {
      return client.get(apiPath`/sales/${id}`, {
        responseSchema: SaleResponseSchema,
      });
    },

    /**
     * Update an existing sale
     * @throws ApiValidationError if request data is invalid
     * @throws ApiRequestError if server returns an error
     */
    updateSale: (id: string, data: UpdateSale): Promise<SaleResponse> => {
      return client.patch(apiPath`/sales/${id}`, {
        data,
        requestSchema: UpdateSaleSchema, // Pre-flight validation
        responseSchema: SaleResponseSchema,
      });
    },

    /**
     * ATL-1445 stage 2b — pay a pay-later sale: books the income keyed by `transactionId` (the
     * Firestore transactions document id), at most once. A replay answers `status: 'replayed'`.
     */
    markSalePaid: (id: string, data: MarkSalePaid): Promise<MarkSalePaidResponse> => {
      return client.post(apiPath`/sales/${id}/payment`, {
        data,
        responseSchema: MarkSalePaidResponseSchema,
      });
    },

    /**
     * Delete a sale
     */
    deleteSale: (id: string): Promise<DeleteResponse> => {
      return client.delete(apiPath`/sales/${id}`, {
        responseSchema: DeleteResponseSchema,
      });
    },

    /**
     * Create a sale return
     * @throws ApiValidationError if request data is invalid
     * @throws ApiRequestError if server returns an error
     */
    createSaleReturn: (saleId: string, data: CreateSaleReturn): Promise<SaleReturnResponse> => {
      return client.post(apiPath`/sales/${saleId}/returns`, {
        data,
        requestSchema: CreateSaleReturnSchema,
        responseSchema: SaleReturnResponseSchema,
      });
    },

    /**
     * Delete a sale return
     */
    deleteSaleReturn: (saleId: string, returnId: string): Promise<DeleteResponse> => {
      return client.delete(apiPath`/sales/${saleId}/returns/${returnId}`, {
        responseSchema: DeleteResponseSchema,
      });
    },

    /**
     * Get all returns for a sale
     */
    getSaleReturns: (saleId: string): Promise<SaleReturnResponse[]> => {
      return client.get(apiPath`/sales/${saleId}/returns`, {
        responseSchema: z.array(SaleReturnResponseSchema),
      });
    },

    /**
     * Get a single sale return
     */
    getSaleReturn: (saleId: string, returnId: string): Promise<SaleReturnResponse> => {
      return client.get(apiPath`/sales/${saleId}/returns/${returnId}`, {
        responseSchema: SaleReturnResponseSchema,
      });
    },

    /**
     * Compare return with Firestore data
     */
    compareReturnWithFirestore: (firestoreId: string): Promise<ReturnComparisonResult> => {
      return client.get(apiPath`/sales/returns/compare/${firestoreId}`, {
        responseSchema: ReturnComparisonResultSchema,
      });
    },
  };
}
